import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTheme, useMediaQuery } from "@mui/material";

const TestimonialModal = ({ open, onClose }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      enqueueSnackbar("Please login to submit a testimonial", { variant: "warning" });
      return;
    }

    if (!comment.trim()) {
      enqueueSnackbar("Please enter a comment", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_BASEURL}/testimonial/add`, {
        user_id: userId,
        ratings: rating,
        comment: comment,
      });

      if (res.data?.success) {
        enqueueSnackbar(res.data.message, { variant: "success" });
        setComment("");
        setRating(5);
        onClose();
      } else {
        enqueueSnackbar(res.data.message || "Failed to submit testimonial", { variant: "error" });
      }
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      enqueueSnackbar("An error occurred. Please try again.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: { 
          borderRadius: { xs: '1.5rem', md: '2.5rem' },
          p: 0,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          mx: { xs: 2, md: 'auto' }, // Add some margin on mobile to prevent touching screen edges
          width: { xs: 'calc(100% - 32px)', md: '100%' }
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '400px' }}>
        {/* Left Side: Branding & Rating */}
        <Box sx={{ 
          flex: 1, 
          bgcolor: '#f8fafc', 
          p: { xs: 3, md: 6 }, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          borderRight: { md: '1px solid #e2e8f0' },
          borderBottom: { xs: '1px solid #e2e8f0', md: 'none' }
        }}>
          <Typography variant={ { xs: 'h5', md: 'h4' } } sx={{ fontWeight: '900', color: '#1e293b', mb: 2, lineHeight: 1.2 }}>
            Share Your <span style={{ color: '#a16207' }}>Experience</span>
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: { xs: '0.9rem', md: '1rem' }, mb: 2, maxWidth: '280px' }}>
            Your feedback helps us improve our collection and services for everyone!
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mb: { xs: 3, md: 6 }, maxWidth: '280px', fontStyle: 'italic' }}>
            *Once the admin approves your feedback, it will reflect in testimonials.
          </Typography>
          
          <Box>
            <Typography sx={{ mb: 2, fontWeight: '800', color: '#1e293b', fontSize: '0.9rem', textTransform: 'uppercase', tracking: '0.1em' }}>
              Your Rating
            </Typography>
            <Rating
              name="testimonial-rating"
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              size="large"
              sx={{
                color: '#f59e0b',
                '& .MuiRating-iconFilled': { color: '#f59e0b' },
                fontSize: { xs: '2.2rem', md: '3rem' }
              }}
            />
          </Box>
        </Box>

        {/* Right Side: Feedback Form */}
        <Box sx={{ flex: 1.2, p: { xs: 3, md: 6 }, display: 'flex', flexDirection: 'column' }}>
          <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ mb: 2, fontWeight: '800', color: '#1e293b', fontSize: '0.9rem', textTransform: 'uppercase', tracking: '0.1em' }}>
              Your Feedback
            </Typography>
            <TextField
              multiline
              rows={isMobile ? 5 : 8}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              placeholder="Tell us what you think about our services and collection..."
              variant="outlined"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '1.5rem',
                  bgcolor: '#ffffff',
                  height: '100%',
                  alignItems: 'flex-start',
                  p: { xs: 2, md: 3 },
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#a16207' },
                }
              }}
            />
          </DialogContent>

          <DialogActions sx={{ p: 0, mt: { xs: 2, md: 4 }, justifyContent: 'flex-end', gap: { xs: 1, md: 2 } }}>
            <Button 
              onClick={onClose} 
              sx={{ 
                color: '#94a3b8', 
                fontWeight: '700',
                px: { xs: 2, md: 3 },
                textTransform: 'none',
                '&:hover': { bgcolor: 'transparent', color: '#64748b' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#a16207',
                '&:hover': { bgcolor: '#854d0e' },
                px: { xs: 3, md: 5 },
                py: { xs: 1.2, md: 1.8 },
                borderRadius: '1.2rem',
                fontWeight: '800',
                textTransform: 'none',
                boxShadow: '0 10px 15px -3px rgba(161, 98, 7, 0.3)',
                '&.Mui-disabled': { bgcolor: '#e2e8f0' }
              }}
            >
              {loading ? "Submitting..." : "Submit Review"}
            </Button>
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TestimonialModal;
